import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hubApi } from '../../lib/hubApi';
import { AdviceCard } from '../../components/shared/AdviceCard';
import { HubPageHeader } from '../../components/hub/HubPageHeader';

type Finding = {
  findingId: string;
  category: 'duplicates' | 'completeness' | 'validity' | 'staleness';
  title: string;
  context: string;
  ruleAdvice: string;
  exportListKey?: string;
};

type Tab = Finding['category'];

const TABS: { id: Tab; label: string }[] = [
  { id: 'duplicates', label: 'Duplicates' },
  { id: 'completeness', label: 'Completeness' },
  { id: 'validity', label: 'Validity' },
  { id: 'staleness', label: 'Staleness & Orphans' },
];

export function HubDataHealthPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('duplicates');
  const [data, setData] = useState<{ subScore: number; findings: Finding[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    hubApi
      .get<{ subScore: number; findings: Finding[] }>('/data-health')
      .then((r) => setData(r.data))
      .catch((err) => {
        const m = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
        setError(m ?? (err as Error).message ?? 'Failed to load data health.');
      })
      .finally(() => setLoading(false));
  }, []);

  const findings = data?.findings.filter((f) => f.category === tab) ?? [];
  const countByTab = (id: Tab) => data?.findings.filter((f) => f.category === id).length ?? 0;

  const subScore = data?.subScore ?? null;
  const subScoreColor =
    subScore === null ? 'text-text' :
    subScore >= 80 ? 'text-success' :
    subScore >= 50 ? 'text-warning' : 'text-danger';

  return (
    <section className="space-y-6 max-w-4xl">
      <HubPageHeader
        title="Data Health"
        subtitle={`${data?.findings.length ?? 0} findings · last audit just now`}
        actions={
          <div className="flex items-baseline gap-1.5 px-3 py-2 rounded-md border border-border-subtle bg-surface-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
              Sub-score
            </span>
            <span className={`text-xl font-semibold tabular-nums leading-none ${subScoreColor}`}>
              {subScore ?? '-'}
            </span>
            <span className="text-xs text-text-muted">/100</span>
          </div>
        }
      />

      {error && (
        <div className="px-4 py-3 rounded-lg border border-danger/40 bg-danger/10 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3.5 py-1.5 rounded-full border text-xs transition-colors ${
              tab === t.id
                ? 'border-orange/60 bg-orange/15 text-orange'
                : 'border-border-subtle bg-surface-2 text-text-muted hover:text-text hover:border-border-strong'
            }`}
          >
            {t.label} <span className="opacity-70">({countByTab(t.id)})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-text-muted text-sm">Loading…</p>
      ) : findings.length === 0 ? (
        <p className="text-text-muted text-sm">No findings in this category.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {findings.map((f) => (
            <AdviceCard
              key={f.findingId}
              findingId={f.findingId}
              title={f.title}
              context={f.context}
              ruleAdvice={f.ruleAdvice}
              exportListKey={f.exportListKey}
              onRequestBPX={() =>
                navigate(`/hub/get-help?findingId=${encodeURIComponent(f.findingId)}`)
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}
